import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Save, Trash2, ArrowLeft, Eye,
  Phone, Mail, User, Loader2, ShieldCheck, Star, CreditCard, StickyNote,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import StatsPanel from "../components/StatsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalizeWhatsapp = (raw) => {
  if (!raw) return "";
  const v = raw.trim();
  if (/wa\.me|whatsapp\.com|https?:\/\//i.test(v)) return v;
  let n = v.replace(/\D/g, "");
  if (!n) return v;
  if (n.startsWith("00")) n = n.slice(2);
  if (n.startsWith("0")) n = "90" + n.slice(1);
  else if (n.length === 10 && n.startsWith("5")) n = "90" + n;
  return `https://wa.me/${n}`;
};

const AdminFirmEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = localStorage.getItem("ds_admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  const [firm, setFirm] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payments, setPayments] = useState([]);
  const [notesList, setNotesList] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [notesBusy, setNotesBusy] = useState(false);

  const fetchFirm = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/firms/${id}/detail`, { headers });
      setFirm(res.data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) navigate("/patron");
      else { toast({ title: "Bulunamadı", description: "Firma getirilemedi." }); navigate("/patron"); }
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (!token) { navigate("/patron"); return; }
    fetchFirm();
    axios.get(`${API}/admin/firms/${id}/payments`, { headers }).then((r) => setPayments(r.data || [])).catch(() => {});
    axios.get(`${API}/admin/firms/${id}/notes`, { headers }).then((r) => setNotesList(r.data || [])).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (firm) {
      setForm({
        name: firm.name || "", category: firm.category || "dugun-salonlari",
        city: firm.city || "Ankara", district: firm.district || "",
        capacity: firm.capacity || "", priceFrom: firm.priceFrom || 0,
        description: firm.description || "", contactName: firm.contactName || "",
        email: firm.email || "", publicPhone: firm.publicPhone || firm.phone || "",
        googleUrl: firm.googleUrl || "",
        whatsappUrl: firm.whatsappUrl || "",
        features: firm.features || [],
      });
    }
  }, [firm]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, priceFrom: Number(form.priceFrom) || 0, whatsappUrl: normalizeWhatsapp(form.whatsappUrl) };
      const res = await axios.put(`${API}/admin/firms/${id}`, payload, { headers });
      setFirm(res.data);
      toast({ title: "Kaydedildi", description: "Firma bilgileri güncellendi." });
    } catch { toast({ title: "Hata", description: "Kaydedilemedi." }); }
    finally { setSaving(false); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setNotesBusy(true);
    try {
      const r = await axios.post(`${API}/admin/firms/${id}/notes`, { text: newNote.trim() }, { headers });
      setNotesList((p) => [r.data, ...p]);
      setNewNote("");
      toast({ title: "Not eklendi" });
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "Not eklenemedi." }); }
    finally { setNotesBusy(false); }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/admin/firms/${id}/notes/${noteId}`, { headers });
      setNotesList((p) => p.filter((n) => n.id !== noteId));
    } catch { toast({ title: "Hata", description: "Not silinemedi." }); }
  };

  if (loading || !form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF5F0]">
        <Loader2 className="w-8 h-8 text-[#9E2A3B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF5F0]">
      <div className="py-6 bg-gradient-to-br from-[#2A2224] to-[#9E2A3B]">
        <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/patron")} className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-white hover:bg-white/25 transition-colors" aria-label="Geri">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <p className="flex items-center gap-1.5 text-white/70 text-xs"><ShieldCheck className="w-3.5 h-3.5" /> Patron • Firma Düzenleme</p>
              <h1 className="font-display text-2xl text-white">{firm?.name}</h1>
            </div>
          </div>
          <Link to={`/mekan/${firm?.slug}`} className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-[#9E2A3B] text-sm font-medium hover:bg-[#F6E4E6] transition-colors">
            <Eye className="w-4 h-4" /> Sitedeki Görünümü
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6">
            <h2 className="font-display text-xl text-[#2A2224] mb-5">İletişim Bilgileri</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Yetkili Adı</Label>
                <div className="relative">
                  <User className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={form.contactName} onChange={(e) => set("contactName", e.target.value)} className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>İletişim Telefonu</Label>
                <div className="relative">
                  <Phone className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={form.publicPhone} onChange={(e) => set("publicPhone", e.target.value)} className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>E-posta</Label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={form.email} onChange={(e) => set("email", e.target.value)} className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                </div>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Google Yorumları Linki</Label>
                <div className="relative">
                  <Star className="w-4 h-4 text-[#C6A15B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    data-testid="firm-google-url-input"
                    value={form.googleUrl}
                    onChange={(e) => set("googleUrl", e.target.value)}
                    placeholder="https://maps.google.com/... veya Google işletme yorum linki"
                    className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]"
                  />
                </div>
                <p className="text-xs text-[#7C6E70]">Bu link girildiğinde işletme sayfasında "Google Yorumları" butonu görünür.</p>
              </div>
            </div>
          </div>

          <StatsPanel stats={firm?.stats} />


          <button onClick={handleSave} disabled={saving}
            className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white font-medium shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-70">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {saving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
          </button>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 lg:sticky lg:top-6" data-testid="admin-firm-payments">
            <h2 className="font-display text-xl text-[#2A2224] mb-1 flex items-center gap-2"><CreditCard className="w-5 h-5 text-[#C6A15B]" /> Ödemeler</h2>
            <p className="text-sm text-[#7C6E70] mb-4">Bu firmanın yaptığı ödemeler.</p>
            {payments.length === 0 ? (
              <p className="text-center text-sm text-[#7C6E70]">Henüz ödeme yok.</p>
            ) : (
              <div className="space-y-2">
                {payments.map((p) => (
                  <div key={p.orderId} data-testid={`admin-payment-${p.orderId}`} className="rounded-xl border border-[#F0DDE0] px-3 py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${p.type === "category" ? "bg-[#FBF3E4] text-[#8a6d1f]" : "bg-[#FBEEF0] text-[#9E2A3B]"}`}>
                        {p.typeLabel}{p.categoryName ? `: ${p.categoryName}` : ""}
                      </span>
                      <span className="font-display font-bold text-[#9E2A3B]">₺{p.amount}</span>
                    </div>
                    <div className="flex items-center justify-between mt-1 text-xs text-[#7C6E70]">
                      <span>{p.paidAt ? new Date(p.paidAt).toLocaleString("tr-TR") : "—"}</span>
                      {p.paymentId && <span className="font-mono opacity-70">#{p.paymentId}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 mt-6" data-testid="admin-firm-notes">
            <h2 className="font-display text-xl text-[#2A2224] mb-1 flex items-center gap-2"><StickyNote className="w-5 h-5 text-[#C6A15B]" /> Notlar</h2>
            <p className="text-sm text-[#7C6E70] mb-4">Bu işletmeyle ilgili özel notlarınız (sadece yöneticiler görür).</p>
            <div className="flex flex-col gap-2 mb-4">
              <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} data-testid="firm-note-input" rows={3} placeholder="İşletmeyle ilgili not yazın..." className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
              <button onClick={addNote} disabled={notesBusy || !newNote.trim()} data-testid="firm-note-add-btn" className="self-end flex items-center gap-2 px-4 h-10 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
                {notesBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <StickyNote className="w-4 h-4" />} Not Ekle
              </button>
            </div>
            {notesList.length === 0 ? (
              <p className="text-center text-sm text-[#7C6E70]">Henüz not eklenmedi.</p>
            ) : (
              <div className="space-y-2">
                {notesList.map((n) => (
                  <div key={n.id} data-testid={`firm-note-${n.id}`} className="rounded-xl border border-[#F0DDE0] bg-[#FBF5F0] px-3 py-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm text-[#2A2224] whitespace-pre-wrap flex-1">{n.text}</p>
                      <button onClick={() => deleteNote(n.id)} title="Sil" className="text-[#c0392b] hover:bg-[#FBE9EA] rounded p-1 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <p className="text-[11px] text-[#a8999b] mt-1">{n.author} · {n.at ? new Date(n.at).toLocaleString("tr-TR") : ""}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminFirmEdit;
